/**
 * Accounting Section - Refactored to use modular ProjectFinanceTab components
 * 
 * This is now a thin wrapper that:
 * 1. Delegates AR/Payments to ProjectFinanceTab (Phase 3 components)
 * 2. Maintains AP (Bills) and Job Costing tabs for backward compatibility
 * 
 * BEFORE: 1020 lines
 * AFTER: ~200 lines (this file) + modular components
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Import new modular components
import { ProjectFinanceTab } from '../finance/ProjectFinanceTab';
import { formatCurrency, formatDate, canUserEdit } from '../finance/ProjectFinanceTab/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status Badge (for bills)
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
    approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
    paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700' },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' }
  };
  const config = statusConfig[status] || { label: status, color: 'bg-slate-100 text-slate-600' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
};

// Bill Modal (retained for AP tab)
const BillModal = ({ isOpen, onClose, onSubmit, bill, isEditing, costCodes }) => {
  const [formData, setFormData] = useState({
    vendor_name: '', bill_number: '', amount: '', description: '',
    cost_code: '', status: 'pending', due_date: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (bill && isEditing) {
      setFormData({
        vendor_name: bill.vendor_name || '', bill_number: bill.bill_number || '',
        amount: bill.amount || '', description: bill.description || '',
        cost_code: bill.cost_code || '', status: bill.status || 'pending',
        due_date: bill.due_date?.split('T')[0] || '', notes: bill.notes || ''
      });
    } else {
      setFormData({ vendor_name: '', bill_number: '', amount: '', description: '',
        cost_code: '', status: 'pending', due_date: '', notes: '' });
    }
  }, [bill, isEditing, isOpen]);

  const handleSubmit = async () => {
    if (!formData.vendor_name || !formData.amount) {
      toast.error('Vendor name and amount are required');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ ...formData, amount: parseFloat(formData.amount) });
      onClose();
    } catch { toast.error('Failed to save bill'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{isEditing ? 'Edit Bill' : 'New Vendor Bill'}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Vendor Name *</Label>
              <Input value={formData.vendor_name} onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })} placeholder="Vendor name" />
            </div>
            <div className="space-y-1.5">
              <Label>Bill Number</Label>
              <Input value={formData.bill_number} onChange={(e) => setFormData({ ...formData, bill_number: e.target.value })} placeholder="BILL-001" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Amount *</Label>
              <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" />
            </div>
            <div className="space-y-1.5">
              <Label>Cost Code</Label>
              <Select value={formData.cost_code} onValueChange={(v) => setFormData({ ...formData, cost_code: v })}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {costCodes?.map(code => (<SelectItem key={code.code} value={code.code}>{code.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Due Date</Label>
              <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Bill description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : isEditing ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
export const AccountingSection = ({ project, user, onRefresh }) => {
  const [accounting, setAccounting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ar');
  const [billModal, setBillModal] = useState({ open: false, bill: null, isEditing: false });

  // Fetch legacy accounting data (for AP and Job Costing tabs)
  const fetchAccounting = useCallback(async () => {
    if (!project?.id) return;
    try {
      const response = await axios.get(`${API}/jobs/${project.id}/accounting`);
      setAccounting(response.data);
    } catch (error) {
      console.error('Error fetching accounting:', error);
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { 
    if (project?.id) fetchAccounting(); 
  }, [project?.id, fetchAccounting]);

  const canEdit = useMemo(() => canUserEdit(user?.role), [user?.role]);

  // Bill handlers
  const handleBillSave = async (data) => {
    if (billModal.isEditing) {
      await axios.patch(`${API}/jobs/${project.id}/bills/${billModal.bill.id}`, data);
    } else {
      await axios.post(`${API}/jobs/${project.id}/bills`, data);
    }
    await fetchAccounting();
    toast.success(billModal.isEditing ? 'Bill updated' : 'Bill created');
  };

  const handleBillDelete = async (billId) => {
    if (!window.confirm('Delete this bill?')) return;
    await axios.delete(`${API}/jobs/${project.id}/bills/${billId}`);
    await fetchAccounting();
    toast.success('Bill deleted');
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;
  }

  if (!project) {
    return <div className="text-center py-12 text-slate-500">No project selected</div>;
  }

  return (
    <div className="space-y-6" data-testid="accounting-section">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="ar">AR (Receivables)</TabsTrigger>
          <TabsTrigger value="ap">AP (Payables)</TabsTrigger>
          <TabsTrigger value="costing">Job Costing</TabsTrigger>
        </TabsList>

        {/* AR Tab - Delegates to new ProjectFinanceTab */}
        <TabsContent value="ar" className="mt-4">
          <ProjectFinanceTab project={project} user={user} onRefresh={onRefresh} />
        </TabsContent>

        {/* AP Tab - Retained from original */}
        <TabsContent value="ap" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-4">
              <CardTitle className="text-base">Vendor Bills & Receipts</CardTitle>
              {canEdit && (
                <Button size="sm" onClick={() => setBillModal({ open: true, bill: null, isEditing: false })}>
                  <Plus className="h-4 w-4 mr-1" /> New Bill
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {accounting?.bills?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Bill #</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Cost Code</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Due Date</TableHead>
                      {canEdit && <TableHead className="w-[80px]"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounting.bills.map(bill => (
                      <TableRow key={bill.id}>
                        <TableCell className="font-medium">{bill.vendor_name}</TableCell>
                        <TableCell>{bill.bill_number || '—'}</TableCell>
                        <TableCell className="text-slate-500">{bill.description || '—'}</TableCell>
                        <TableCell>{bill.cost_code || '—'}</TableCell>
                        <TableCell className="text-right">{formatCurrency(bill.amount)}</TableCell>
                        <TableCell><StatusBadge status={bill.status} /></TableCell>
                        <TableCell>{formatDate(bill.due_date)}</TableCell>
                        {canEdit && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setBillModal({ open: true, bill, isEditing: true })}>Edit</Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleBillDelete(bill.id)}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-slate-500 text-center py-8">No bills yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Job Costing Tab - Retained from original */}
        <TabsContent value="costing" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Budget vs Actual by Cost Code</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {accounting?.cost_by_code?.map(code => {
                  const budgetPercent = code.budget > 0 ? (code.actual / code.budget) * 100 : 0;
                  const isOverBudget = code.actual > code.budget && code.budget > 0;
                  return (
                    <div key={code.code} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{code.name}</span>
                        <span className={`text-sm ${isOverBudget ? 'text-red-600' : 'text-slate-500'}`}>
                          {formatCurrency(code.actual)} / {formatCurrency(code.budget)}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div className={`h-full transition-all ${isOverBudget ? 'bg-red-500' : budgetPercent > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(budgetPercent, 100)}%` }} />
                      </div>
                      {isOverBudget && <p className="text-xs text-red-600">Over budget by {formatCurrency(code.actual - code.budget)}</p>}
                    </div>
                  );
                })}
                {(!accounting?.cost_by_code || accounting.cost_by_code.length === 0) && (
                  <p className="text-slate-500 text-center py-8">No cost data available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bill Modal */}
      <BillModal
        isOpen={billModal.open}
        onClose={() => setBillModal({ open: false, bill: null, isEditing: false })}
        onSubmit={handleBillSave}
        bill={billModal.bill}
        isEditing={billModal.isEditing}
        costCodes={accounting?.cost_codes}
      />
    </div>
  );
};

export default AccountingSection;
